/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { addAppealRequest } from "services/appealService";
import {
  AppealRequest,
  BailRight,
  CaseCreation,
  CountDefendantsList,
  FinalVerdict,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData?: CaseCreation;
  appeal?: AppealRequest;
  bailRight?: BailRight;
  appealType: "PRE_JUDGEMENT" | "FINAL_VERDICT" | "Case" | "BAILRIGHT";
  count?: CountDefendantsList;
  finalVerdict?: FinalVerdict;
  onClose: (refetch: boolean) => void;
  isCassation?: boolean;
};

const AppealRequestForm: FC<Props> = ({
  caseData,
  appeal,
  bailRight,
  appealType,
  count,
  finalVerdict,
  onClose,
  isCassation = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<AppealRequest>({
    appeal_type: appealType,
    status: "PENDING",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      [name]: value,
    }));
  };

  const handleFile = (file: File | null) => {
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      appeal_summary_files: file,
    }));
  };

  const validateForm = () => {
    if (!formState?.reason) {
      return t("reason_is_required");
    }
    if (!formState?.appeal_summary_text) {
      return t("appeal_summary_is_required");
    }
    if (!formState?.appeal_summary_files) {
      return t("appeal_file_is_required");
    }
    // if (!formState?.defendant_ids || formState?.defendant_ids?.length === 0) {
    //   return "Defendant is required.";
    // }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    setLoading(true);
    const data = {
      ...formState,
      appeal_summary_files: formState?.appeal_summary_files,
      case_id: caseData?.id || appeal?.case_id,
      parent_appeal: appeal?.id || undefined,
      opening_case_file: caseData?.opening_case_files?.[0]?.id || null,
      bail_right_id: appeal?.bail_right?.uu_id || bailRight?.uu_id || undefined,
      is_cassation: isCassation || false,
    };

    addAppealRequest(data)
      .then(() => {
        showTooltip(t("successfully_requested"), "success");
        navigate("/appeal");
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if ((caseData?.counts || appeal || bailRight) && appealType) {
      setFormState((prev) => ({
        ...prev,
        case_id: caseData?.id || appeal?.case_id,
        count_id:
          count?.id ||
          finalVerdict?.count_defendant_info?.id ||
          appeal?.count_id,
        final_verdict_id: finalVerdict?.id || appeal?.final_verdict_id,
        crime_registration_number:
          caseData?.crime_refernce_number ||
          appeal?.crime_registration_number ||
          bailRight?.crime_registration_number,
        incident_registration_number:
          caseData?.incident_registration_no ||
          appeal?.incident_registration_number ||
          bailRight?.incident_registration_number,
        prosecutor_registration_number:
          caseData?.prosecutor_registration_number ||
          appeal?.prosecutor_registration_number ||
          bailRight?.prosecutor_registration_number,
        status: "PENDING",
        court_registration_number: caseData?.opening_case_files
          ? caseData?.opening_case_files?.[0]?.court_registration_number
          : appeal?.court_registration_number ||
            bailRight?.court_registration_number ||
            "",

        reason: "",
        appeal_type: appealType,
        defendant_ids: appeal?.defendants
          ? appeal?.defendants?.map((i) => String(i?.id))
          : [],
        appeal_summary_file: null,
        appeal_summary_text: "",
        admin_unit_id: loginData?.admin_unit + "",
        org_unit_id: loginData?.org_unit + "",
        requesting_prosecutor_id: loginData?.user_id + "",
        crime_type:
          finalVerdict?.count_defendant_info?.crime_type ||
          count?.crime_type ||
          appeal?.crime_type ||
          "",
        parent_appeal: appeal?.id || undefined,
        bail_right_id: appeal?.bail_right?.uu_id,
      }));
    }
  }, [caseData, appealType, appeal, bailRight]);

  // if (!finalVerdict && !count) return <></>;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t(isCassation ? "cassation_request" : "appeal_request")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {isCassation
                  ? t("request_cassation_form_desc", {
                      defaultValue:
                        "Request a cassation against this verdict — fill in the details below.",
                    })
                  : t("request_appeal_form_desc", {
                      defaultValue:
                        "Request an appeal against this verdict — fill in the details below.",
                    })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            {!appeal && !bailRight && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("select_count_defendant")}
                </label>
                <select
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-32"
                  name="defendant_ids"
                  value={
                    Array.isArray(formState?.defendant_ids)
                      ? formState.defendant_ids.map((file: any) =>
                          typeof file === "object"
                            ? String(file.uu_id ?? file.id)
                            : String(file),
                        )
                      : []
                  }
                  onChange={(e) => {
                    const selectedIds = Array.from(
                      e.target.selectedOptions,
                    ).map((option) => option.value);
                    setFormState((prev) => ({
                      ...prev,
                      defendant_ids: selectedIds.map((id) => id),
                    }));
                  }}
                  multiple
                >
                  {(
                    count?.defendants_info ||
                    finalVerdict?.count_defendant_info?.defendants_info ||
                    caseData?.case_defendant_links
                  )?.map((def: any, idx: number) => (
                    <option key={def.id} value={def?.id}>
                      {def?.defendant_info?.first_name}{" "}
                      {def?.defendant_info?.middle_name}{" "}
                      {def?.defendant_info?.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {bailRight && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("suspect_full_name")}
                </label>
                <input
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="defendent_name_if_bail_right"
                  value={formState?.defendent_name_if_bail_right ?? ""}
                  onChange={handleChange}
                />
              </div>
            )}
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("reason")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="reason"
                value={formState?.reason ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("appeal_summary")}
              </label>
              <textarea
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="appeal_summary_text"
                value={formState?.appeal_summary_text ?? ""}
                onChange={handleChange}
                rows={4}
              />
            </div>
            <div className="mt-4">
              <FileUpload
                label={t("claim_summary_document")}
                fileTypes="*"
                info={t("upload_files")}
                required={false}
                onChange={handleFile}
              />
            </div>
          </div>
            <div className="flex justify-end gap-3 mt-2 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading", { defaultValue: "Loading..." })}
                  </>
                ) : (
                  t("request")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppealRequestForm;
